'use memo';

import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AgxColumn, AgxRow, Images } from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { LoadingState } from 'utils/loadingState';
import { updateAgent, updateFeatureToggles } from 'store/agentInfoReducer';
import { useFetchAvailableForms } from 'features/forms/formsReducer';
import AutopilotLogo from './assets/images/Autopilot-logo.svg?react';
import IconBubble from './components/IconBubble';
import { applyTheme, useFirstPaintLoader } from '@urbanx/agx.ui-components2';
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client';
import { useFetchAgencyInfo } from 'features/agencies/agenciesReducer';
import {
  AgencyInfoAccountType,
  useGetFeatureToggles,
} from '@urbanx/agx.agencies.client';
import { shouldHideReportingDashboard } from 'features/reportingDashboard/reportingDashboardAccess';
import AppConfig from 'config/AppConfig';
import DevMenuButton from './components/DevMenuButton';
import './BrandedLayout.scss';

interface BrandedLayoutProps {
  children: ReactElement | ReactElement[];
}

const isFinishedLoadingCampaigns = (loadingState: LoadingState) => {
  return (
    loadingState === LoadingState.Loaded ||
    loadingState === LoadingState.Failed
  );
};

const hexToRgba = (hex: string | undefined, alpha = 1) => {
  if (!hex) return undefined;
  const [r, g, b] = hex.match(/\w\w/g)?.map(x => parseInt(x, 16)) ?? [0, 0, 0];
  return `rgba(${r},${g},${b},${alpha})`;
};

const hexToRgb = (hex: string | undefined) => {
  if (!hex) return undefined;
  const [r, g, b] = hex.match(/\w\w/g)?.map(x => parseInt(x, 16)) ?? [0, 0, 0];
  return `${r}, ${g}, ${b}`;
};

const attemptToSetDocumentStyle = (
  variable: string,
  value: string | undefined,
  opacity: string | undefined = undefined
) => {
  if (value == null) return;

  const usableValue = `${value}${opacity ?? ''}`;

  document.documentElement.style.setProperty(variable, usableValue);
};

const isDevScenarioEnvironment = () =>
  ['localhost', '127.0.0.1'].includes(window.location.hostname) ||
  AppConfig.envSigninPrefix === 'dev' ||
  AppConfig.envSigninPrefix === 'stg';

const hasStaffRole = (roles: readonly string[] | undefined) =>
  Object.values(StaffRole).some(role => roles?.includes(role));

const BrandedLayout = (props: BrandedLayoutProps) => {
  const { children } = props;
  const { userAccount } = useContext(AuthContext);
  const { pathname } = useLocation();

  useFetchAgencyInfo();
  const fetchAvailableForms = useFetchAvailableForms();
  const { data: featureToggles, isPending: featureTogglesLoading } =
    useGetFeatureToggles();
  const {
    loadingState: agencyInfoLoadingState,
    info: { accountType, branding },
  } = useAppSelector(state => state.agencies.agencyInfo);
  const { availableForms, loadingState: formsLoadingState } = useAppSelector(
    state => state.forms
  );
  const { loadingState: campaignsLoadingState } = useAppSelector(
    state => state.campaigns
  );
  const canBeginForm = useMemo(() => {
    return (
      formsLoadingState === LoadingState.Loaded &&
      availableForms.some(af => af.beginsCampaign)
    );
  }, [formsLoadingState, availableForms]);

  const dispatch = useAppDispatch();
  const hideReportingDashboard = shouldHideReportingDashboard(accountType);
  const hideDashboardHeaderCard =
    accountType === AgencyInfoAccountType.Autopilot;
  const showDevScenarioMenu =
    isDevScenarioEnvironment() && hasStaffRole(userAccount?.roles);

  useEffect(() => {
    if (userAccount) {
      dispatch(
        updateAgent({
          agency: userAccount.agency ?? null,
          agent: userAccount.agent ?? null,
          accountType,
        })
      );
    }
  }, [accountType, userAccount, dispatch, updateAgent]);

  useEffect(() => {
    if (!featureTogglesLoading && featureToggles) {
      dispatch(updateFeatureToggles(featureToggles));
    }
  }, [featureTogglesLoading, featureToggles, dispatch, updateFeatureToggles]);

  useEffect(() => {
    fetchAvailableForms();
  }, []);

  const navigate = useNavigate();

  const updateBranding = useCallback(() => {
    if (!branding) return;

    const generatedBrandingColours = branding?.brandingColours;

    if (generatedBrandingColours) {
      applyTheme(
        {
          main: generatedBrandingColours.generatedMainColours ?? [],
          accent: generatedBrandingColours.generatedAccentColours ?? [],
        },
        'agent-portal',
        true
      );
    }

    attemptToSetDocumentStyle(
      '--brand-primary-color',
      branding.backgroundColor
    );

    attemptToSetDocumentStyle(
      '--brand-primary-color-rgb',
      hexToRgb(branding.backgroundColor)
    );
    attemptToSetDocumentStyle(
      '--brand-text-primary-color',
      branding.backgroundTextColor
    );
    attemptToSetDocumentStyle(
      '--brand-text-primary-color-rgb',
      hexToRgb(branding.backgroundTextColor)
    );
    attemptToSetDocumentStyle(
      '--brand-secondary-color',
      branding.accentColorOld
    );
    attemptToSetDocumentStyle(
      '--brand-outline-color',
      branding.accentColorOld,
      '20'
    );
    attemptToSetDocumentStyle(
      '--brand-secondary-color-alpha90',
      branding.accentColorOld,
      '90'
    );
    attemptToSetDocumentStyle(
      '--brand-text-button-color',
      branding.buttonTextColor
    );

    if (branding.sidebarBackgroundColorOpacity != null) {
      attemptToSetDocumentStyle(
        '--brand-sidebar-background-color',
        hexToRgba(
          branding.sidebarBackgroundColor,
          parseInt(branding.sidebarBackgroundColorOpacity) * 0.01
        )
      );
    }
    if (branding.inboxBackgroundColorOpacity != null) {
      attemptToSetDocumentStyle(
        '--brand-inbox-background-color',
        hexToRgba(
          branding.inboxBackgroundColor,
          parseInt(branding.inboxBackgroundColorOpacity) * 0.01
        )
      );
    }
  }, [branding]);

  useEffect(() => {
    if (agencyInfoLoadingState === LoadingState.Loaded) {
      updateBranding();
    }
  }, [agencyInfoLoadingState, updateBranding]);

  const propertyLogo = useMemo(() => {
    return branding?.logo?.publicPath;
  }, [branding]);

  useEffect(() => {
    if (
      formsLoadingState === LoadingState.Loaded &&
      agencyInfoLoadingState === LoadingState.Loaded &&
      !canBeginForm &&
      !hideReportingDashboard &&
      pathname === '/'
    ) {
      navigate('/dashboard');
    }
  }, [
    agencyInfoLoadingState,
    formsLoadingState,
    canBeginForm,
    hideReportingDashboard,
    pathname,
  ]);

  useFirstPaintLoader({
    isAppReady:
      agencyInfoLoadingState === LoadingState.Loaded &&
      formsLoadingState === LoadingState.Loaded,
    isInitialLoadComplete: isFinishedLoadingCampaigns(campaignsLoadingState),
  });

  return (
    <AgxColumn extraClasses="brandedLayout" data-testid="agx-brandedLayout">
      <AgxRow extraClasses="brandedHeader" centered spaceBetween>
        {propertyLogo ? (
          <img
            className="headerLogo"
            src={propertyLogo as string}
            alt="Property Logo"
          />
        ) : (
          <AutopilotLogo className="headerLogo" />
        )}

        {(!hideDashboardHeaderCard || showDevScenarioMenu) && (
          <AgxRow extraClasses="dashboardHeader-card">
            {!hideDashboardHeaderCard && (
              <>
                {canBeginForm && (
                  <IconBubble
                    image={<Images.HomeOutline />}
                    onClick={() => {
                      navigate('/');
                    }}
                  />
                )}
                <IconBubble
                  image={<Images.TargetOutline />}
                  onClick={() => {
                    navigate('/dashboard');
                  }}
                />
              </>
            )}
            {showDevScenarioMenu && <DevMenuButton />}
          </AgxRow>
        )}
      </AgxRow>
      <div>{children}</div>
    </AgxColumn>
  );
};

export default BrandedLayout;
